import apiClient from '@/api/client';
import { FACES } from '@/api/endpoints';
import type { PaginatedResponse } from '@/api/types/common.types';
import type {
  AmbiguousResponse,
  FaceActionResponse,
  SplitPersonResponse,
  ClusterStartResponse,
  DecisionResponse,
  Person,
  PersonCluster,
  PersonDetail,
  PersonFilters,
  PersonMergePayload,
  PersonPhotosResponse,
  PersonUpdatePayload,
  ReclusterResponse,
  UnassignedFacesResponse,
  WizardDecisionPayload,
  WizardStatus,
} from '@/api/types/face.types';

// ---------------------------------------------------------------------------
// Persons
// ---------------------------------------------------------------------------

/** Fetch paginated list of persons with optional filters. */
export async function getPersons(
  params?: PersonFilters,
): Promise<PaginatedResponse<Person>> {
  const { data } = await apiClient.get<PaginatedResponse<Person>>(
    FACES.PERSONS,
    { params },
  );
  return data;
}

/** Fetch a single person by ID with face detections. */
export async function getPerson(id: string): Promise<PersonDetail> {
  const { data } = await apiClient.get<PersonDetail>(FACES.PERSON_DETAIL(id));
  return data;
}

/** Update a person's name and/or role. */
export async function updatePerson(
  id: string,
  payload: PersonUpdatePayload,
): Promise<PersonDetail> {
  const { data } = await apiClient.patch<PersonDetail>(
    FACES.PERSON_DETAIL(id),
    payload,
  );
  return data;
}

/** Soft-delete a person (deactivate and unassign faces). */
export async function deletePerson(id: string): Promise<void> {
  await apiClient.delete(FACES.PERSON_DETAIL(id));
}

/** Merge two persons into one. */
export async function mergePersons(
  payload: PersonMergePayload,
): Promise<PersonDetail> {
  const { data } = await apiClient.post<PersonDetail>(
    FACES.PERSON_MERGE,
    payload,
  );
  return data;
}

/** Fetch photos containing a specific person. */
export async function getPersonPhotos(
  personId: string,
  params?: { page?: number; page_size?: number },
): Promise<PersonPhotosResponse> {
  const { data } = await apiClient.get<PersonPhotosResponse>(
    FACES.PERSON_PHOTOS(personId),
    { params },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Face Wizard
// ---------------------------------------------------------------------------

/** Get the current wizard/clustering status for an event. */
export async function getWizardStatus(eventId: string): Promise<WizardStatus> {
  const { data } = await apiClient.get<WizardStatus>(FACES.WIZARD_STATUS, {
    params: { event: eventId },
  });
  return data;
}

/** Start face clustering for an event. */
export async function startClustering(
  eventId: string,
): Promise<ClusterStartResponse> {
  const { data } = await apiClient.post<ClusterStartResponse>(
    FACES.WIZARD_START,
    { event: eventId },
  );
  return data;
}

/** Fetch all person clusters for the wizard review step. */
export async function getWizardClusters(
  eventId: string,
): Promise<PersonCluster[]> {
  const { data } = await apiClient.get<PersonCluster[]>(
    FACES.WIZARD_CLUSTERS,
    { params: { event: eventId } },
  );
  return data;
}

/** Fetch the next batch of ambiguous face pairs. */
export async function getAmbiguousPairs(
  eventId: string,
  limit?: number,
): Promise<AmbiguousResponse> {
  const { data } = await apiClient.get<AmbiguousResponse>(
    FACES.WIZARD_AMBIGUOUS,
    { params: { event: eventId, limit } },
  );
  return data;
}

/** Submit merge/split decisions for ambiguous pairs. */
export async function submitDecisions(
  eventId: string,
  payload: WizardDecisionPayload,
): Promise<DecisionResponse> {
  const { data } = await apiClient.post<DecisionResponse>(
    FACES.WIZARD_DECIDE,
    { event: eventId, ...payload },
  );
  return data;
}

/** Trigger re-clustering with user decisions applied. */
export async function recluster(eventId: string): Promise<ReclusterResponse> {
  const { data } = await apiClient.post<ReclusterResponse>(
    FACES.WIZARD_RECLUSTER,
    { event: eventId },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Face Reassignment
// ---------------------------------------------------------------------------

/** Remove faces from a person (unassign them). */
export async function removeFaces(
  personId: string,
  faceIds: string[],
): Promise<FaceActionResponse> {
  const { data } = await apiClient.post<FaceActionResponse>(
    FACES.PERSON_REMOVE_FACES(personId),
    { face_ids: faceIds },
  );
  return data;
}

/** Assign unassigned faces to a person. */
export async function assignFaces(
  personId: string,
  faceIds: string[],
): Promise<FaceActionResponse> {
  const { data } = await apiClient.post<FaceActionResponse>(
    FACES.PERSON_ASSIGN_FACES(personId),
    { face_ids: faceIds },
  );
  return data;
}

/** Fetch unassigned faces for an event. */
export async function getUnassignedFaces(
  eventId: string,
): Promise<UnassignedFacesResponse> {
  const { data } = await apiClient.get<UnassignedFacesResponse>(
    FACES.WIZARD_UNASSIGNED,
    { params: { event: eventId } },
  );
  return data;
}


/** Split a person cluster by a seed face. */
export async function splitPerson(
  personId: string,
  faceId: string,
): Promise<SplitPersonResponse> {
  const { data } = await apiClient.post<SplitPersonResponse>(
    FACES.PERSON_SPLIT(personId),
    { face_id: faceId },
  );
  return data;
}
